import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Alert,
  Avatar,
  CircularProgress,
  Backdrop,
  Snackbar,
  Tab,
  Tabs,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Assignment as ContractIcon,
  Description as ServiceIcon,
  SwapHoriz as ConvertIcon,
  FolderShared as DriveIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';

// Currency options with USD as default
const currencyOptions = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK'
];

// Client status options
const clientStatusOptions = ['Active', 'Inactive', 'Suspended'];

export default function Clients() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, isSuperAdmin, isPartner } = useAuth();
  const { 
    getCountryOptions, 
    getIndustryOptions, 
    loading: configLoading 
  } = useConfig();
  
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [partnerEntities, setPartnerEntities] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogTab, setDialogTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConvertDialog, setOpenConvertDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [clientContacts, setClientContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    location: '',
    relationshipManagerUid: '',
    status: 'Active'
  });
  const [formData, setFormData] = useState({
    clientName: '',
    industry: '',
    location: '',
    currency: 'USD',
    relationshipManagerUid: '',
    googleDriveURL: '',
    status: 'Active',
    // Address fields
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    // Banking fields
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    iban: '',
    swiftCode: ''
  });

  // Get dynamic configuration options
  const countries = getCountryOptions();
  const industries = getIndustryOptions();
  const sortedIndustries = Array.isArray(industries)
    ? [...industries].sort((a, b) => a.localeCompare(b))
    : [];

  // Fetch partner entities for relationship manager dropdown
  const fetchPartnerEntities = async () => {
    try {
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, where('entityType', '==', 'partner'), where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      const partnersData = [];
      snapshot.forEach((doc) => {
        partnersData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setPartnerEntities(partnersData);
    } catch (error) {
      console.error('Error fetching partner entities:', error);
    }
  };

  // Fetch leads for conversion
  const fetchLeads = async () => {
    try {
      const leadsRef = collection(db, 'leads');
      const q = query(leadsRef, where('status', '==', 'Heating Up'));
      const snapshot = await getDocs(q);
      const leadsData = [];
      snapshot.forEach((doc) => {
        leadsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setLeads(leadsData);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  // Check if client name is unique
  const checkClientNameUnique = async (clientName, excludeId = null) => {
    try {
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('clientName', '==', clientName));
      const snapshot = await getDocs(q);
      
      // If editing, exclude current client from uniqueness check
      if (excludeId) {
        return snapshot.docs.filter(doc => doc.id !== excludeId).length === 0;
      }
      
      return snapshot.docs.length === 0;
    } catch (error) {
      console.error('Error checking client name uniqueness:', error);
      return false;
    }
  };

  // Firestore operations
  const fetchClients = async () => {
    try {
      setLoading(true);
      const clientsRef = collection(db, 'clients');
      
      let q;
      // For now, show all clients until we properly migrate the relationship manager IDs
      // TODO: Implement proper filtering once relationship manager IDs are migrated from user IDs to partner entity IDs
      q = query(clientsRef, orderBy('clientName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const clientsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          clientsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString()?.split('T')[0] || new Date().toISOString().split('T')[0],
          });
        });
        setClients(clientsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching clients:', error);
      setSnackbar({ open: true, message: 'Error loading clients', severity: 'error' });
      setLoading(false);
    }
  };

  const addClient = async (clientData) => {
    try {
      setSaving(true);
      
      // Check uniqueness
      const isUnique = await checkClientNameUnique(clientData.clientName);
      if (!isUnique) {
        throw new Error('A client with this name already exists. Client creation aborted.');
      }

      const docRef = await addDoc(collection(db, 'clients'), {
        ...clientData,
        status: clientData.status || 'Active',
        createdAt: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Client added successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding client:', error);
      setSnackbar({ open: true, message: error.message || 'Error adding client', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateClient = async (clientId, clientData) => {
    try {
      setSaving(true);
      
      // Check uniqueness if client name is being changed
      const currentClient = clients.find(c => c.id === clientId);
      if (currentClient && currentClient.clientName !== clientData.clientName) {
        const isUnique = await checkClientNameUnique(clientData.clientName, clientId);
        if (!isUnique) {
          throw new Error('A client with this name already exists. Update aborted.');
        }
      }
      
      const updateData = { ...clientData };
      delete updateData.createdFromLeadId; // Immutable
      
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, updateData);
      
      setSnackbar({ open: true, message: 'Client updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating client:', error);
      setSnackbar({ open: true, message: error.message || 'Error updating client', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Lead conversion functionality
  const convertLeadToClient = async (leadData) => {
    try {
      setSaving(true);
      
      // Check if company name is unique for client
      const isUnique = await checkClientNameUnique(leadData.businessName);
      if (!isUnique) {
        throw new Error('A client with this name already exists. Conversion aborted.');
      }

      const batch = writeBatch(db);
      
      // Create client record
      const clientRef = doc(collection(db, 'clients'));
      const clientData = {
        clientName: leadData.businessName,
        industry: leadData.industry || '',
        location: leadData.location || '',
        currency: 'USD',
        relationshipManagerUid: leadData.relationshipManagerUid || (partnerEntities.length > 0 ? partnerEntities[0].id : ''),
        googleDriveURL: '',
        createdFromLeadId: leadData.id,
        status: 'Active',
        createdAt: serverTimestamp(),
      };
      batch.set(clientRef, clientData);

      // Create contact record
      const contactRef = doc(collection(db, 'contacts'));
      const contactData = {
        name: leadData.contactName,
        email: leadData.email || '',
        phone: leadData.phone || '',
        clientId: clientRef.id,
        isPrimary: true,
        createdAt: serverTimestamp(),
      };
      batch.set(contactRef, contactData);

      // Update lead status to Converted
      const leadRef = doc(db, 'leads', leadData.id);
      batch.update(leadRef, { 
        status: 'Converted',
        convertedAt: serverTimestamp(),
        convertedToClientId: clientRef.id
      });

      await batch.commit();
      
      setSnackbar({ 
        open: true, 
        message: `Lead "${leadData.contactName}" successfully converted to client!`, 
        severity: 'success' 
      });
      
      // Refresh leads list
      fetchLeads();
      
    } catch (error) {
      console.error('Error converting lead:', error);
      setSnackbar({ open: true, message: error.message || 'Error converting lead', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const unsubscribe = fetchClients();
    fetchPartnerEntities();
    fetchLeads();
    // Apply clientId filter from query string if present
    try {
      const params = new URLSearchParams(location.search);
      const clientId = params.get('clientId');
      if (clientId) {
        setActiveTab(0);
        setFilters((prev) => ({ ...prev, status: '', industry: '', location: '' }));
        setFilteredClients((prev) => prev); // no-op to ensure state init
        // Defer until clients load; another effect below will narrow by clientId
      }
    } catch {}
    
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [filters, clients, activeTab]);

  const applyFilters = () => {
    let filtered = clients;
    // Query param: clientId takes precedence when present
    const params = new URLSearchParams(location.search);
    const clientIdParam = params.get('clientId');
    if (clientIdParam) {
      filtered = clients.filter(c => c.id === clientIdParam);
      setFilteredClients(filtered);
      return;
    }
    
    // Tab-based filtering
    if (activeTab === 0 && isPartner() && !isSuperAdmin()) {
      // My Clients - already filtered by fetchClients for Partners
      filtered = clients;
    } else if (activeTab === 0 && isSuperAdmin()) {
      // My Clients for SA - show all
      filtered = clients;
    }
    
    // Additional filters
    if (filters.industry) {
      filtered = filtered.filter(client => client.industry === filters.industry);
    }
    if (filters.location) {
      filtered = filtered.filter(client => client.location === filters.location);
    }
    if (filters.status) {
      filtered = filtered.filter(client => (client.status || 'Active') === filters.status);
    }
    // Relationship manager filter (supports post-migration entity IDs)
    if (filters.relationshipManagerUid) {
      filtered = filtered.filter(
        (client) => client.relationshipManagerUid === filters.relationshipManagerUid
      );
    }
    
    setFilteredClients(filtered);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        clientName: client.clientName,
        industry: client.industry,
        location: client.location,
        currency: client.currency,
        relationshipManagerUid: client.relationshipManagerUid,
        googleDriveURL: client.googleDriveURL || '',
        status: client.status || 'Active',
        // Address fields
        streetAddress: client.streetAddress || '',
        city: client.city || '',
        state: client.state || '',
        postalCode: client.postalCode || '',
        country: client.country || '',
        // Banking fields
        bankName: client.bankName || '',
        accountNumber: client.accountNumber || '',
        routingNumber: client.routingNumber || '',
        iban: client.iban || '',
        swiftCode: client.swiftCode || ''
      });
    } else {
      setEditingClient(null);
      // Do not auto-select a relationship manager; require explicit choice
      setFormData({
        clientName: '',
        industry: '',
        location: '',
        currency: 'USD',
        relationshipManagerUid: '',
        googleDriveURL: '',
        status: 'Active',
        // Address fields
        streetAddress: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        // Banking fields
        bankName: '',
        accountNumber: '',
        routingNumber: '',
        iban: '',
        swiftCode: ''
      });
    }
    setDialogTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
  };

  // Fetch contacts linked to a client for the dialog Contacts tab
  const fetchClientContacts = async (clientId) => {
    if (!clientId) {
      setClientContacts([]);
      return;
    }
    try {
      setContactsLoading(true);
      const contactsRef = collection(db, 'contacts');
      const qContacts = query(contactsRef, where('associatedClient', '==', clientId));
      const snapshot = await getDocs(qContacts);
      const contactsData = [];
      snapshot.forEach((doc) => {
        contactsData.push({ id: doc.id, ...doc.data() });
      });
      setClientContacts(contactsData);
    } catch (error) {
      console.error('Error fetching client contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  // Load contacts when the Contacts tab is focused in the dialog
  useEffect(() => {
    if (openDialog && editingClient && dialogTab === 3) {
      fetchClientContacts(editingClient.id);
    }
  }, [openDialog, editingClient, dialogTab]);

  const handleOpenConvertDialog = (lead) => {
    setSelectedLead(lead);
    setOpenConvertDialog(true);
  };

  const handleCloseConvertDialog = () => {
    setOpenConvertDialog(false);
    setSelectedLead(null);
  };

  const handleSubmit = async () => {
    try {
      // Basic validation
      if (!formData.clientName.trim()) {
        setSnackbar({ open: true, message: 'Client name is required', severity: 'error' });
        return;
      }
      
      if (!formData.relationshipManagerUid) {
        setSnackbar({ open: true, message: 'Relationship Manager is required', severity: 'error' });
        return;
      }

      if (editingClient) {
        await updateClient(editingClient.id, formData);
      } else {
        await addClient(formData);
      }
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleConvertLead = async () => {
    try {
      await convertLeadToClient(selectedLead);
      handleCloseConvertDialog();
    } catch (error) {
      // Error handling is done in convertLeadToClient function
    }
  };

  const getPartnerName = (entityId) => {
    if (!entityId) return 'No Partner Assigned';
    
    // Try to find partner entity first
    const partner = partnerEntities.find(p => p.id === entityId);
    if (partner) {
      return partner.entityName;
    }
    
    // Fallback: This might be a legacy user ID - show a temporary message
    return 'Legacy Assignment (needs update)';
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  // Tab labels based on role
  const getTabLabels = () => {
    if (isSuperAdmin()) {
      return ['All Clients'];
    } else {
      return ['My Clients' ];
    }
  };

  const getClientStatusProps = (status) => {
    const normalized = (status || 'Active').toLowerCase();
    switch (normalized) {
      case 'active':
        return { label: 'Active', color: 'success' };
      case 'inactive':
        return { label: 'Inactive', color: 'default' };
      case 'suspended':
        return { label: 'Suspended', color: 'warning' };
      default:
        return { label: status || 'Active', color: 'default' };
    }
  };

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Clients Management
            </Typography>
           
          </Box>
        </Box>
        <Box>
          {/* <Button
            startIcon={<ConvertIcon />}
            onClick={() => fetchLeads()}
            sx={{ mr: 1 }}
            variant="outlined"
          >
            Convert Lead
          </Button> */}
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Client
          </Button>
        </Box>
      </Box>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          {getTabLabels().map((label, index) => (
            <Tab key={index} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={filters.industry}
                  label="Industry"
                  onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                >
                  <MenuItem value="">All Industries</MenuItem>
                  {sortedIndustries.map(industry => (
                    <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.location}
                  label="Country"
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map(country => (
                    <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isSuperAdmin() && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Relationship Manager</InputLabel>
                  <Select
                    value={filters.relationshipManagerUid}
                    label="Relationship Manager"
                    onChange={(e) => setFilters({ ...filters, relationshipManagerUid: e.target.value })}
                  >
                    <MenuItem value="">All Managers</MenuItem>
                    {partnerEntities.map(partner => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.entityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  {clientStatusOptions.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}



      {/* Clients Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Client </TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Drive</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="h6" color="text.secondary">No clients found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                          {client.clientName?.charAt(0) || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {client.clientName || 'Unknown Client'}
                          </Typography>
                          {client.createdFromLeadId && (
                            <Typography variant="caption" color="text.secondary">
                              Converted from Lead
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{client.industry || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {client.location || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {getPartnerName(client.relationshipManagerUid)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const { label, color } = getClientStatusProps(client.status);
                        return (
                          <Chip 
                            label={label}
                            color={color}
                            size="small"
                            icon={<ActiveIcon />}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {client.googleDriveURL ? (
                        <Tooltip title="Open Google Drive">
                          <IconButton
                            size="small"
                            component={Link}
                            href={client.googleDriveURL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <DriveIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(client)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Delete functionality completely removed per spec */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Client Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingClient ? 'Edit Client' : 'Add Client'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" autoComplete="off">
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={dialogTab} onChange={(e, newValue) => setDialogTab(newValue)}>
              <Tab label="Basic Information" />
              <Tab label="Address" />
              <Tab label="Banking Information" />
              <Tab label="Contacts" />
            </Tabs>
            </Box>

          {/* Basic Information Tab */}
          {dialogTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Client Name"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  required
                  helperText="Must be unique across the system"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={formData.industry}
                    label="Industry"
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  >
                    <MenuItem value="">Select Industry</MenuItem>
                    {sortedIndustries.map(industry => (
                      <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Location (Country)</InputLabel>
                  <Select
                    value={formData.location}
                    label="Location (Country)"
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  >
                    <MenuItem value="">Select Country</MenuItem>
                    {countries.map(country => (
                      <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {clientStatusOptions.map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Relationship Manager</InputLabel>
                  <Select
                    value={formData.relationshipManagerUid}
                    label="Relationship Manager"
                    onChange={(e) => setFormData({ ...formData, relationshipManagerUid: e.target.value })}
                    required
                  >
                    <MenuItem value="">Select Partner</MenuItem>
                    {partnerEntities.map(partner => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.entityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Google Drive URL"
                  value={formData.googleDriveURL}
                  onChange={(e) => setFormData({ ...formData, googleDriveURL: e.target.value })}
                  placeholder="https://drive.google.com/drive/folders/..."
                  helperText="External link to folder for contract, proposal, or asset sharing"
                />
              </Grid>
            </Grid>
          )}

          {/* Address Tab */}
          {dialogTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                  placeholder="123 Main Street"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New York"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NY"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="10001"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.country}
                    label="Country"
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  >
                    <MenuItem value="">Select Country</MenuItem>
                    {countries.map(country => (
                      <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Banking Information Tab */}
          {dialogTab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Banking information is used for payment processing and financial transactions.
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bankName"
                  autoComplete="off"
                  spellCheck={false}
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  placeholder="JPMorgan Chase Bank"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Account Number"
                  name="bankAccountNumber"
                  autoComplete="off"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  placeholder="1234567890"
                  type="text"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Routing Number"
                  name="bankRoutingNumber"
                  autoComplete="off"
                  value={formData.routingNumber}
                  onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value })}
                  placeholder="021000021"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IBAN"
                  name="bankIban"
                  autoComplete="off"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                  placeholder="GB29 NWBK 6016 1331 9268 19"
                  helperText="International Bank Account Number (for international transfers)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="SWIFT Code"
                  name="bankSwiftCode"
                  autoComplete="off"
                  value={formData.swiftCode}
                  onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value.toUpperCase() })}
                  placeholder="CHASUS33"
                  helperText="Bank Identifier Code for international wire transfers"
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
            </Grid>
          )}

          {/* Contacts Tab */}
          {dialogTab === 3 && (
            <Box>
              {!editingClient ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Save the client first to view associated contacts.
                </Alert>
              ) : contactsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : clientContacts.length === 0 ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No contacts linked to this client yet.
                  </Alert>
                  <Button variant="outlined" onClick={() => navigate(`/contacts?clientId=${editingClient.id}`)}>
                    Go to Contacts
                  </Button>
                </Box>
              ) : (
                <List>
                  {clientContacts.map((contact) => (
                    <ListItem key={contact.id} disablePadding>
                      <ListItemButton onClick={() => navigate(`/contacts?clientId=${editingClient.id}`)}>
                        <Avatar sx={{ width: 28, height: 28, mr: 2 }}>
                          {contact.fullName?.charAt(0) || '?'}
                        </Avatar>
                        <ListItemText
                          primary={contact.fullName || 'Untitled Contact'}
                          secondary={contact.email || contact.titleRole || ''}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {editingClient ? 'Update' : 'Add'}
            {saving && <CircularProgress size={24} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Conversion Dialog */}
      <Dialog open={openConvertDialog} onClose={handleCloseConvertDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Convert Lead to Client</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will create a new Client record and Contact record, then mark the Lead as "Converted".
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Lead Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Contact Name</Typography>
                  <Typography variant="body1">{selectedLead.contactName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Business Name</Typography>
                  <Typography variant="body1">{selectedLead.businessName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedLead.email || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedLead.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">Conversion Result</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    • Client Name: {selectedLead.businessName}<br/>
                    • Contact Name: {selectedLead.contactName}<br/>
                    • Relationship Manager: {getPartnerName(selectedLead.relationshipManagerUid)}<br/>
                    • Status: Active
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertDialog}>Cancel</Button>
          <Button 
            onClick={handleConvertLead} 
            variant="contained" 
            color="success"
            disabled={saving}
            startIcon={<ConvertIcon />}
          >
            Convert to Client
            {saving && <CircularProgress size={24} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

 
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
} 